import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Container,
  AppBar,
  Toolbar,
  useTheme,
  useMediaQuery,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  List,
  ListItem,
  Divider,
  Alert
} from '@mui/material';
import {
  TableRestaurant,
  RestaurantMenu,
  Euro,
  CheckCircle,
  Person,
  EventSeat,
  Menu as MenuIcon,
  Print,
  Assessment,
  Settings,
  Receipt,
  HelpOutline,
  Wifi,
  PhoneAndroid,
  NetworkCheck
} from '@mui/icons-material';
import { useStore } from '../store/useStore';
import { Table } from '../types';
import BergLogo from '../assets/berg-regions.svg';
import BellaItaliaLogo from '../assets/logo-bella-italia.png';
import MBPLCLogo from '../assets/logo-mitchells-butlers.svg';
import { useLanguage } from '../contexts/LanguageContext';
import { useCustomTheme } from '../contexts/ThemeContext';


const TablesOverview: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { currentBrand } = useCustomTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isLandscape = useMediaQuery('(orientation: landscape)');
  const isTabletLandscape = isTablet && isLandscape;
  const { tables, initializeData } = useStore();
  const { t } = useLanguage();
  
  const [tapCount, setTapCount] = useState(0);
  const tapTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [dialogOpen, setDialogOpen] = useState<'print' | 'diagnostics' | 'transactions' | 'help' | null>(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [transactions, setTransactions] = useState<any[]>([]);

  useEffect(() => {
    if (tables.length === 0) {
      initializeData();
    }
  }, [tables.length, initializeData]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    // Generate static transaction data once when component mounts
    const generateTransactions = () => {
      const now = new Date();
      const transactionList = [];
      for (let i = 0; i < 10; i++) {
        const time = new Date(now.getTime() - i * 30 * 60000); // Every 30 minutes
        const subtotal = Math.floor(Math.random() * 150) + 30;
        const tipPercent = [0, 5, 10, 15][Math.floor(Math.random() * 4)];
        const tip = subtotal * (tipPercent / 100);
        transactionList.push({
          id: `TRX-${1000 + i}`,
          time: time.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
          tableNumber: Math.floor(Math.random() * 20) + 1,
          subtotal,
          tip,
          total: subtotal + tip,
          paymentMethod: Math.random() > 0.3 ? 'Card' : 'Cash'
        });
      }
      return transactionList;
    };
    
    setTransactions(generateTransactions());
  }, []);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleMenuItemClick = (action: 'print' | 'diagnostics' | 'transactions' | 'help') => {
    handleMenuClose();
    setDialogOpen(action);
  };

  const handlePrintReport = () => {
    setIsPrinting(true);
    setTimeout(() => {
      setIsPrinting(false);
      setDialogOpen(null);
    }, 2000);
  };

  const getDailyReportData = () => {
    // Calculate statistics from transactions
    const cardTransactions = transactions.filter(t => t.paymentMethod === 'Card');
    const cashTransactions = transactions.filter(t => t.paymentMethod === 'Cash');
    
    const cardTotal = cardTransactions.reduce((sum, t) => sum + t.subtotal, 0);
    const cardTips = cardTransactions.reduce((sum, t) => sum + t.tip, 0);
    const cashTotal = cashTransactions.reduce((sum, t) => sum + t.subtotal, 0);
    const cashTips = cashTransactions.reduce((sum, t) => sum + t.tip, 0);
    
    return {
      date: new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }),
      totalOrders: transactions.length,
      cardOrders: cardTransactions.length,
      cashOrders: cashTransactions.length,
      cardSubtotal: cardTotal,
      cardTips: cardTips,
      cardTotal: cardTotal + cardTips,
      cashSubtotal: cashTotal,
      cashTips: cashTips,
      cashTotal: cashTotal + cashTips,
      totalSubtotal: cardTotal + cashTotal,
      totalTips: cardTips + cashTips,
      grandTotal: cardTotal + cashTotal + cardTips + cashTips,
      averageTicket: (cardTotal + cashTotal + cardTips + cashTips) / transactions.length || 0,
      averageTip: ((cardTips + cashTips) / (cardTotal + cashTotal) * 100) || 0
    };
  };

  const getDiagnostics = () => {
    const isOnline = navigator.onLine;
    const connection = (navigator as any).connection;
    const userAgent = navigator.userAgent;
    const isAndroid = /Android/i.test(userAgent);
    
    return {
      online: isOnline,
      connectionType: connection?.effectiveType || 'Unknown',
      downlink: connection?.downlink ? `${connection.downlink} Mbps` : 'Unknown',
      rtt: connection?.rtt ? `${connection.rtt}ms` : 'Unknown',
      device: isAndroid ? 'Android Device' : 'Other Device',
      userAgent: userAgent.substring(0, 100) + '...',
      screenSize: `${window.screen.width}x${window.screen.height}`,
      browserSize: `${window.innerWidth}x${window.innerHeight}`
    };
  };


  const handleBergLogoTap = () => {
    // Clear existing timeout
    if (tapTimeoutRef.current) {
      clearTimeout(tapTimeoutRef.current);
    }

    const newTapCount = tapCount + 1;
    setTapCount(newTapCount);

    if (newTapCount === 5) {
      // Trigger clearSettings after 5 taps
      if (window.BtDevice && window.BtDevice.clearSettings) {
        window.BtDevice.clearSettings();
        console.log('BtDevice.clearSettings() called');
      }
      setTapCount(0);
    } else {
      // Set timeout to reset tap count after 2 seconds
      tapTimeoutRef.current = setTimeout(() => {
        setTapCount(0);
      }, 2000);
    }
  };

  const getStatusColor = (status: Table['status']) => {
    switch (status) {
      case 'free':
        return 'success';
      case 'awaiting-order':
        return 'warning';
      case 'waiting-payment':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: Table['status']) => {
    switch (status) {
      case 'free':
        return <CheckCircle />;
      case 'awaiting-order':
        return <RestaurantMenu />;
      case 'waiting-payment':
        return <Euro />;
      default:
        return <TableRestaurant />;
    }
  };

  const getStatusText = (status: Table['status']) => {
    switch (status) {
      case 'free':
        return t('tableStatus.available');
      case 'awaiting-order':
        return t('tableStatus.awaitingOrder');
      case 'waiting-payment':
        return t('tableStatus.readyForPayment');
      default:
        return status;
    }
  };

  const handleTableClick = (tableId: string) => {
    navigate(`/table/${tableId}`);
  };

  const getOrderTotal = (table: Table) => {
    const activeOrder = table.orders.find(o => o.status === 'pending');
    return activeOrder ? activeOrder.total : 0;
  };

  const getReservationTimeInfo = (reservation: Table['reservation']) => {
    if (!reservation || !reservation.time) return null;
    
    const now = new Date();
    const [hours, minutes] = reservation.time.split(':').map(Number);
    const reservationTime = new Date();
    reservationTime.setHours(hours, minutes, 0, 0);
    
    // If reservation time is earlier than now, assume it's for tomorrow
    if (reservationTime < now) {
      reservationTime.setDate(reservationTime.getDate() + 1);
    }
    
    const diffMs = reservationTime.getTime() - now.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);
    
    let timeString = '';
    let bgColor = '#e0e0e0'; // Grey for > 1 hour
    let textColor = '#616161';
    
    if (diffMinutes < 0) {
      timeString = 'Past due';
      bgColor = '#ffebee';
      textColor = '#c62828';
    } else if (diffMinutes < 30) {
      const mins = diffMinutes;
      timeString = `${mins}m`;
      bgColor = '#ffebee'; // Light red
      textColor = '#c62828'; // Dark red
    } else if (diffMinutes < 60) {
      const mins = diffMinutes;
      timeString = `${mins}m`;
      bgColor = '#fff3e0'; // Light orange
      textColor = '#e65100'; // Dark orange
    } else {
      const hours = Math.floor(diffMinutes / 60);
      const mins = diffMinutes % 60;
      if (hours > 0 && mins > 0) {
        timeString = `${hours}h ${mins}m`;
      } else if (hours > 0) {
        timeString = `${hours}h`;
      } else {
        timeString = `${mins}m`;
      }
      bgColor = '#f5f5f5'; // Light grey
      textColor = '#616161'; // Dark grey
    }
    
    return { timeString, bgColor, textColor, diffMinutes };
  };

  const freeTables = tables.filter(t => t.status === 'free');
  const awaitingTables = tables.filter(t => t.status === 'awaiting-order');
  const paymentTables = tables.filter(t => t.status === 'waiting-payment');

  return (
    <Box sx={{ 
      flexGrow: 1, 
      bgcolor: theme.palette.background.default, 
      minHeight: '100vh'
    }}>
      <AppBar position="sticky" sx={{
        background: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
        borderBottom: 'none'
      }}>
        <Toolbar sx={{ minHeight: { xs: 64, sm: 72 }, justifyContent: 'space-between' }}>
          {/* Brand Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {currentBrand.id === 'mbplc' ? (
              <img
                src={MBPLCLogo}
                alt="MBPLC Logo"
                style={{
                  width: 'auto',
                  height: isMobile ? 32 : 40,
                  objectFit: 'contain',
                  filter: theme.palette.primary.contrastText === '#ffffff' ? 'brightness(0) invert(1)' : 'none'
                }}
              />
            ) : (
              <img
                src={BellaItaliaLogo}
                alt="Bella Italia Logo"
                style={{
                  width: 'auto',
                  height: isMobile ? 28 : 36,
                  filter: theme.palette.primary.contrastText === '#ffffff' ? 'none' : 'brightness(0) invert(1)'
                }}
              />
            )}
          </Box>
          
          {/* Center Time Display */}
          <Box sx={{ 
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)'
          }}>
            <Typography 
              variant={isMobile ? "h5" : "h4"} 
              sx={{ 
                fontWeight: 400,
                letterSpacing: '0.02em',
                fontSize: isMobile ? '1.5rem' : '1.8rem'
              }}
            >
              {currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}
            </Typography>
          </Box>

          {/* Right side - Menu and Berg Logo */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            gap: 1
          }}>
            <IconButton
              onClick={handleMenuOpen}
              sx={{ color: theme.palette.primary.contrastText }}
            >
              <MenuIcon />
            </IconButton>
            <img
              src={BergLogo}
              alt="Berg Logo"
              onClick={handleBergLogoTap}
              style={{
                width: 'auto',
                height: isMobile ? 32 : 40,
                filter: theme.palette.primary.contrastText === '#ffffff' ? 'none' : 'brightness(0) invert(1)',
                cursor: 'pointer'
              }}
            />
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ 
        flex: 1,
        mt: { xs: 2, sm: 3 }, 
        mb: { xs: 2, sm: 3 }, 
        px: { xs: 1, sm: 2, md: isLandscape ? 3 : 2 } 
      }}>
        {/* Status Summary Cards - Compact */}
        <Box sx={{ mb: 2 }}>
          <Grid container spacing={1.5}>
            <Grid size={{ xs: 4, sm: 4 }}>
              <Card sx={{ 
                background: '#ffffff',
                borderLeft: '3px solid #b5cbb3'
              }}>
                <CardContent sx={{ p: { xs: 1, sm: 1.5 }, py: { xs: 0.75, sm: 1 } }}>
                  <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 400, color: '#b5cbb3', lineHeight: 1 }}>
                    {freeTables.length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? '0.65rem' : '0.75rem' }}>
                    {t('tableStatus.available')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 4, sm: 4 }}>
              <Card sx={{ 
                background: '#ffffff',
                borderLeft: '3px solid #ffa726'
              }}>
                <CardContent sx={{ p: { xs: 1, sm: 1.5 }, py: { xs: 0.75, sm: 1 } }}>
                  <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 400, color: '#ffa726', lineHeight: 1 }}>
                    {awaitingTables.length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? '0.65rem' : '0.75rem' }}>
                    {t('tableStatus.awaitingOrder')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 4, sm: 4 }}>
              <Card sx={{ 
                background: '#ffffff',
                borderLeft: '3px solid #CE2B37'
              }}>
                <CardContent sx={{ p: { xs: 1, sm: 1.5 }, py: { xs: 0.75, sm: 1 } }}>
                  <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 400, color: '#CE2B37', lineHeight: 1 }}>
                    {paymentTables.length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? '0.65rem' : '0.75rem' }}>
                    {t('tableStatus.readyForPayment')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Tables Grid */}
        <Grid container spacing={{ xs: 1, sm: 2, md: 2 }}>
          {tables.map((table) => (
            <Grid size={{ 
              xs: 6, 
              sm: isTabletLandscape ? 3 : 4, 
              md: isLandscape ? 2 : 3, 
              lg: 2,
              xl: 2
            }} key={table.id}>
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  minHeight: { 
                    xs: 140, 
                    sm: isTabletLandscape ? 140 : 160,
                    md: isLandscape ? 150 : 160
                  },
                  position: 'relative',
                  overflow: 'visible',
                  '&:hover': {
                    transform: isTablet ? 'none' : 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)'
                  },
                  '&:active': {
                    transform: 'scale(0.98)',
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: table.status === 'free' ? '#b5cbb3' : 
                                 table.status === 'awaiting-order' ? '#ffa726' : '#CE2B37'
                  }
                }}
                onClick={() => handleTableClick(table.id)}
              >
                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                    <Box>
                      <Typography 
                        variant={isMobile ? "h6" : "h5"} 
                        component="div" 
                        sx={{ fontWeight: 500 }}
                      >
                        T{table.number}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <Person sx={{ fontSize: isMobile ? 14 : 16, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}>
                          {table.seats} {t('common.seats')}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ fontSize: { xs: 20, sm: 24 }, color: 'text.secondary' }}>
                      {getStatusIcon(table.status)}
                    </Box>
                  </Box>

                  <Chip 
                    label={getStatusText(table.status)}
                    color={getStatusColor(table.status)}
                    size="small"
                    sx={{ 
                      fontWeight: 500,
                      fontSize: isMobile ? '0.7rem' : '0.75rem',
                      height: 24
                    }}
                  />
                  
                  {table.status === 'waiting-payment' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <Typography 
                        variant="body2" 
                        sx={{ fontWeight: 600, color: '#CE2B37' }}
                      >
                        €{getOrderTotal(table).toFixed(2)}
                      </Typography>
                    </Box>
                  )}

                  {table.reservation && (() => {
                    const timeInfo = getReservationTimeInfo(table.reservation);
                    return (
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 0.5, 
                        mt: 1,
                        p: 0.5,
                        bgcolor: timeInfo?.bgColor || '#f5f5f5',
                        borderRadius: 1,
                        overflow: 'hidden'
                      }}>
                        <EventSeat sx={{ fontSize: 14, color: timeInfo?.textColor || '#616161', flexShrink: 0 }} />
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, overflow: 'hidden', minWidth: 0 }}>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: timeInfo?.textColor || '#616161', 
                              fontWeight: 600, 
                              lineHeight: 1,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {table.reservation.time} - {table.reservation.name}
                          </Typography>
                          {timeInfo && (
                            <Typography variant="caption" sx={{ color: timeInfo.textColor, fontSize: '0.65rem', fontWeight: 500 }}>
                              {timeInfo.timeString} {t('common.remaining')}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })()}

                  {table.orders.length > 0 && (
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ 
                        display: 'block',
                        mt: 1,
                        fontSize: '0.7rem'
                      }}
                    >
                      {table.orders.length} {table.orders.length > 1 ? t('common.orders').toLowerCase() : t('common.order').toLowerCase()}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Menu Popup */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleMenuItemClick('print')}>
          <ListItemIcon>
            <Assessment />
          </ListItemIcon>
          <ListItemText>{t('reports.dailyReport')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick('diagnostics')}>
          <ListItemIcon>
            <Settings />
          </ListItemIcon>
          <ListItemText>{t('diagnostics.systemDiagnostics')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick('transactions')}>
          <ListItemIcon>
            <Receipt />
          </ListItemIcon>
          <ListItemText>{t('reports.lastTransactions')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick('help')}>
          <ListItemIcon>
            <HelpOutline />
          </ListItemIcon>
          <ListItemText>{t('support.getHelp')}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Daily Report Dialog */}
      <Dialog open={dialogOpen === 'print'} onClose={() => setDialogOpen(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Assessment />
          {t('reports.dailyReport')}
        </DialogTitle>
        <DialogContent>
          {isPrinting ? (
            <Box sx={{ textAlign: 'center', py: 5 }}>
              <CircularProgress size={60} sx={{ mb: 3 }} />
              <Typography variant="h6">{t('reports.preparingReport')}</Typography>
            </Box>
          ) : (
            <>
              {(() => {
                const report = getDailyReportData();
                return (
                  <>
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h6" gutterBottom>{report.date}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('reports.dailySalesSummary')}
                      </Typography>
                    </Box>

                    {/* Order Statistics */}
                    <Box sx={{ mb: 3, p: 2, bgcolor: theme.palette.background.default, borderRadius: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                        {t('reports.orderStatistics')}
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid size={4}>
                          <Typography variant="body2" color="text.secondary">{t('reports.totalOrders')}</Typography>
                          <Typography variant="h5">{report.totalOrders}</Typography>
                        </Grid>
                        <Grid size={4}>
                          <Typography variant="body2" color="text.secondary">{t('reports.averageTicket')}</Typography>
                          <Typography variant="h5">€{report.averageTicket.toFixed(2)}</Typography>
                        </Grid>
                        <Grid size={4}>
                          <Typography variant="body2" color="text.secondary">{t('reports.averageTip')}</Typography>
                          <Typography variant="h5">{report.averageTip.toFixed(1)}%</Typography>
                        </Grid>
                      </Grid>
                    </Box>

                    {/* Payment Breakdown */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                        {t('reports.paymentBreakdown')}
                      </Typography>
                      
                      {/* Card Payments */}
                      <Box sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                          {t('reports.cardPayments')} ({report.cardOrders} {t('common.orders').toLowerCase()})
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2">{t('common.subtotal')}:</Typography>
                          <Typography variant="body2">€{report.cardSubtotal.toFixed(2)}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2">{t('common.tips')}:</Typography>
                          <Typography variant="body2" color="success.main">€{report.cardTips.toFixed(2)}</Typography>
                        </Box>
                        <Divider sx={{ my: 1 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{t('common.total')}:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>€{report.cardTotal.toFixed(2)}</Typography>
                        </Box>
                      </Box>

                      {/* Cash Payments */}
                      <Box sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                          {t('reports.cashPayments')} ({report.cashOrders} {t('common.orders').toLowerCase()})
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2">{t('common.subtotal')}:</Typography>
                          <Typography variant="body2">€{report.cashSubtotal.toFixed(2)}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2">{t('common.tips')}:</Typography>
                          <Typography variant="body2" color="success.main">€{report.cashTips.toFixed(2)}</Typography>
                        </Box>
                        <Divider sx={{ my: 1 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{t('common.total')}:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>€{report.cashTotal.toFixed(2)}</Typography>
                        </Box>
                      </Box>
                    </Box>

                    {/* Grand Total */}
                    <Box sx={{ p: 2, bgcolor: theme.palette.primary.main, borderRadius: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="h6">{t('reports.dailyTotal')}:</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>€{report.grandTotal.toFixed(2)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">{t('reports.totalTips')}:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>€{report.totalTips.toFixed(2)}</Typography>
                      </Box>
                    </Box>
                  </>
                );
              })()}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setDialogOpen(null)} 
            variant="contained"
            sx={{
              backgroundColor: theme.palette.grey ? theme.palette.grey[600] : '#9e9e9e',
              '&:hover': {
                backgroundColor: theme.palette.grey ? theme.palette.grey[700] : '#757575'
              }
            }}
          >
            {t('common.close')}
          </Button>
          <Button 
            onClick={handlePrintReport} 
            variant="contained" 
            startIcon={<Print />}
            disabled={isPrinting}
            sx={{
              backgroundColor: theme.palette.grey ? theme.palette.grey[800] : '#424242',
              color: '#ffffff',
              '&:hover': {
                backgroundColor: theme.palette.grey ? theme.palette.grey[900] : '#303030'
              }
            }}
          >
            {t('reports.printReport')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diagnostics Dialog */}
      <Dialog open={dialogOpen === 'diagnostics'} onClose={() => setDialogOpen(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Settings />
          {t('diagnostics.systemDiagnostics')}
        </DialogTitle>
        <DialogContent>
          <List>
            <ListItem>
              <ListItemIcon><Wifi /></ListItemIcon>
              <ListItemText 
                primary={t('diagnostics.internetConnection')} 
                secondary={getDiagnostics().online ? t('diagnostics.connected') : t('diagnostics.disconnected')}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><NetworkCheck /></ListItemIcon>
              <ListItemText 
                primary={t('diagnostics.connectionType')} 
                secondary={getDiagnostics().connectionType}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><NetworkCheck /></ListItemIcon>
              <ListItemText 
                primary={t('diagnostics.networkSpeed')} 
                secondary={`Downlink: ${getDiagnostics().downlink}, RTT: ${getDiagnostics().rtt}`}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><PhoneAndroid /></ListItemIcon>
              <ListItemText 
                primary={t('diagnostics.deviceType')} 
                secondary={getDiagnostics().device}
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary={t('diagnostics.screenResolution')} 
                secondary={getDiagnostics().screenSize}
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary={t('diagnostics.browserWindow')} 
                secondary={getDiagnostics().browserSize}
              />
            </ListItem>
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(null)} variant="contained">
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transactions Dialog */}
      <Dialog open={dialogOpen === 'transactions'} onClose={() => setDialogOpen(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Receipt />
          {t('reports.lastTransactions')}
        </DialogTitle>
        <DialogContent>
          <List>
            {transactions.map((transaction, index) => (
              <React.Fragment key={transaction.id}>
                <ListItem>
                  <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {transaction.id} - Table {transaction.tableNumber}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {transaction.time} • {transaction.paymentMethod}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="subtitle1">
                        €{transaction.total.toFixed(2)}
                      </Typography>
                      {transaction.tip > 0 && (
                        <Typography variant="caption" color="success.main">
                          {t('common.tip')}: €{transaction.tip.toFixed(2)}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </ListItem>
                {index < 9 && <Divider />}
              </React.Fragment>
            ))}
          </List>
          <Box sx={{ mt: 2, p: 2, bgcolor: theme.palette.background.default, borderRadius: 1 }}>
            <Typography variant="h6">
              {t('reports.dailyTotal')}: €{transactions.reduce((sum, t) => sum + t.total, 0).toFixed(2)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('common.tips')}: €{transactions.reduce((sum, t) => sum + t.tip, 0).toFixed(2)}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(null)} variant="contained">
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Help Dialog */}
      <Dialog open={dialogOpen === 'help'} onClose={() => setDialogOpen(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HelpOutline />
          {t('support.getHelp')}
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('support.supportMessage')}
          </Alert>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t('support.supportContact')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {t('support.email')}: <strong>support@bergregions.pl</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('support.businessHours')}
              {t('support.emergencyNote')}
            </Typography>
          </Box>
          <Box sx={{ mt: 2, p: 2, bgcolor: theme.palette.background.default, borderRadius: 1 }}>
            <Typography variant="body2">
              {t('support.systemVersion')}: 1.0.0<br />
              {t('support.lastUpdated')}: {new Date().toLocaleDateString()}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(null)} variant="contained">
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TablesOverview;